import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  getRouterSelectors,
  SerializedRouterStateSnapshot,
} from '@ngrx/router-store';
import {
  createActionGroup,
  props,
  emptyProps,
  createFeature,
  createReducer,
  createSelector,
} from '@ngrx/store';
import { tap } from 'rxjs';
import { StringLiteralCheck } from 'src/app/shared/util/string-literal-check';

// Configurable query param names
export interface DrilldownQueryParamConfig {
  details: string;
}

export const DEFAULT_DRILLDOWN_QUERY_PARAMS_CONFIG: DrilldownQueryParamConfig =
  {
    details: 'details',
  };

export interface DrilldownFeatureConfig {
  routeFilter: (route: SerializedRouterStateSnapshot) => boolean;
  queryParamsConfig?: DrilldownQueryParamConfig;
}

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @param config Required configuration: router and filtering.
 * @returns NGRX feature, actions, and effect builder.
 *
 * The NGRX feature implements URL-driven data flow for drilldowns.  The object
 * to be drilled down is identified by a string key, which becomes the value of
 * the query parameter "details".
 *
 * The component receives the drilldown key via the `selectDrilldownKey`
 * selector.  The drilldown key is either a string identifying the object for
 * drilldown, or `null` meaning that drilldown is not requested.
 *
 * The following actions are supported:
 *   drilldown(key): request drilldown for the object identified with the string `key`
 *   backFromDrilldown(): close drilldown
 *
 * The effect builder is a function returning effects that should be embedded in
 * page's effects.  Internally, the effects perform routing tasks.
 */
export function createDrilldownFeature<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
>(featureName: TActionSource, config: DrilldownFeatureConfig) {
  const queryParamsConfig =
    config.queryParamsConfig ?? DEFAULT_DRILLDOWN_QUERY_PARAMS_CONFIG;
  const actions = createActionGroup({
    source: featureName,
    events: {
      Drilldown: props<{ key: string }>(),
      'Back From Drilldown': emptyProps(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer(null),
    extraSelectors: () => {
      const { selectQueryParams } = getRouterSelectors();
      const selectDrilldownKey = createSelector(
        selectQueryParams,
        queryParams => queryParams?.[queryParamsConfig.details],
      );
      return { selectDrilldownKey };
    },
  });
  const createEffects = (action$: Actions, router: Router) => ({
    drilldown$: createEffect(
      () =>
        action$.pipe(
          ofType(actions.drilldown),
          tap(action => {
            const queryParams = { [queryParamsConfig.details]: action.key };
            router.navigate([], { queryParams, queryParamsHandling: 'merge' });
          }),
        ),
      { dispatch: false },
    ),
    backFromDrilldown$: createEffect(
      () =>
        action$.pipe(
          ofType(actions.backFromDrilldown),
          tap(() => {
            const queryParams = { [queryParamsConfig.details]: null };
            router.navigate([], { queryParams, queryParamsHandling: 'merge' });
          }),
        ),
      { dispatch: false },
    ),
  });
  return { feature, actions, createEffects };
}
